import { Box, Button, ButtonGroup, InputLabel, Tooltip, Typography } from '@mui/material';
import React, { useMemo, useState, useEffect, useRef } from 'react';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useDispatch } from 'react-redux';
import { updateFormatOption } from '../../../redux/ChartPoperties/ChartControlsActions';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux';

interface Props {
  propKey: string;
  handleUpdateFormat: (option: string, value: any, optionKey?: string) => void;
  formatObject: any;
}

/**
 *
 * @param propKey propkey of a particular tile
 * @returns Ui piece for date format. It includes various format options for date/month/year and contians format and reset button. Format button allows to format date for selected measure and reset button reset the date to original format.
 */
const DateFormat = ({ propKey, handleUpdateFormat, formatObject }: Props) => {
  const [editMode, setEditMode] = useState(false);
  const [sourcesMap, setSourceMap] = useState<Map<string, string>>(new Map());
  const [sourcesFormatMapping, setSourcesFormatMapping] = useState<Map<string, string>>(new Map());

  const { chartOptionSelected } = useSelector((state: RootState) => ({
    chartOptionSelected: state.chartProperties?.properties[propKey]?.chartOptionSelected,
  }));

  const dispatch = useDispatch();

  const formatMapping = useMemo(
    () => ({
      Year: [
        {
          label: '2025',
          format: 'yyyy',
          description: 'Year as a numeric, 4-digit value',
        },
        {
          label: '25',
          format: 'yy',
          description: 'Year as a numeric, 2-digit value',
        },
      ],
      Month: [
        {
          label: 'January',
          format: 'MMMM',
          description: 'Month name in full (January to December)',
        },
        {
          label: 'Jan',
          format: 'MMM',
          description: 'Abbreviated month name (Jan to Dec)',
        },
        {
          label: '1',
          format: 'M',
          description: 'Numeric month name (0 to 12)',
        },
        {
          label: '01',
          format: 'MM',
          description: 'Month name as a numeric value (00 to 12)',
        },
      ],
      Day: [
        {
          label: '01',
          format: 'dd',
          description: 'Day of the month as a numeric value (01 to 31)',
        },
        {
          label: '1',
          format: 'd',
          description: 'Day of the month as a numeric value (0 to 31)',
        },
        {
          label: '1st',
          format: 'do',
          description:
            'Day of the month as a numeric value, followed by suffix (1st, 2nd, 3rd, ...)',
        },
      ],
      Separator: [
        {
          label: '/',
          format: '/',
          description: 'Separator is a forward slash',
        },
        {
          label: '-',
          format: '-',
          description: 'Separator is a hyphen',
        },
        {
          label: 'Custom',
          // format: customSeparator,
          description: 'Add a custom separator',
        },
      ],
    }),
    [],
  );

  useEffect(() => {
    const measureId = formatObject?.selectedMeasure?.uId;
    if (!measureId) return;
    const savedString = formatObject?.measureFormats?.[measureId]?.finalDateFormat ?? '';

    // Reset if nothing is saved and we switched measures
    if (!savedString) {
      setSourceMap(new Map());
      setSourcesFormatMapping(new Map());
      return;
    }

    const currentUIString = Array.from(sourcesFormatMapping.values()).join('');
    if (savedString === currentUIString) return;

    // This regex splits the string into tokens (yyyy, MMMM, -, etc.)
    const tokenRegex = /(yyyy|yy|MMMM|MMM|MM|M|dd|do|d|[^a-zA-Z0-9])/gi;
    const parts = savedString.match(tokenRegex) || [];

    const newSourceMap = new Map();
    const newFormatMap = new Map();
    let sepCount = 1;

    parts.forEach((part: string) => {
      let key = '';
      let label = '';
      const lowerPart = part.toLowerCase();

      if (lowerPart.includes('y')) {
        key = 'year';
        label =
          formatMapping.Year.find((y) => y.format.toLowerCase() === lowerPart)?.label ||
          (lowerPart === 'yyyy' ? '2025' : '25');
      } else if (lowerPart.includes('m')) {
        key = 'month';
        const foundMonth = formatMapping.Month.find(
          (m) => m.format.toUpperCase() === part.toUpperCase(),
        );
        label = foundMonth ? foundMonth.label : part;
      } else if (lowerPart.includes('d')) {
        key = 'date';
        label = formatMapping.Day.find((d) => d.format.toLowerCase() === lowerPart)?.label || part;
      } else {
        key = `separator${sepCount++}`;
        label = part;
      }

      newSourceMap.set(key, label);
      newFormatMap.set(key, part);
    });

    setSourceMap(newSourceMap);
    setSourcesFormatMapping(newFormatMap);
  }, [formatObject?.selectedMeasure?.uId, formatObject?.measureFormats, chartOptionSelected]);

  function getMapKeyByValue(mapObject: Map<string, string>, searchValue: string) {
    for (const [key, value] of mapObject.entries()) {
      if (value === searchValue) {
        return key;
      }
    }
  }

  // const formatTypeKey = 'dateFormats';
  const [activeButton, setActiveButton] = useState<string>('');
  const hasOutput = sourcesMap.size > 0;
  const buttonOptions: any[] = [
    { name: 'Format', title: 'Format Date' },
    { name: 'Reset', hintTitle: 'Click to Reset Date', title: 'Reset to Original' },
  ];

  const RenderButtons: any = () =>
    buttonOptions.map((option: any) => {
      const isActive = activeButton === option.name;
      return (
        <div
          key={option.name}
          className="radioButton buttonOptions"
          style={{
            backgroundColor: isActive ? '#e0e0e0' : '#ffffff',

            cursor: !hasOutput ? 'not-allowed' : 'pointer',
            opacity: !hasOutput ? 0.6 : 1,
            pointerEvents: 'auto',
            border: '1px solid #ccc',
            padding: '0px 10px',
            //borderRadius: '4px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
            marginBottom: '10px',
          }}
          onClick={() => {
            if (!hasOutput) return;
            setActiveButton(option.name);
            const measureId = formatObject?.selectedMeasure?.uId;
            if (!measureId) return;
            const joinedFormat = Array.from(sourcesFormatMapping.values()).join('');
            const optionKey = chartOptionSelected === 'Tooltip' ? 'tooltipFormats' : 'labelFormats';

            handleUpdateFormat(
              'measureFormats',
              {
                ...formatObject.measureFormats,
                [measureId]: {
                  ...formatObject.measureFormats?.[measureId],

                  // Reset clears date, Format sets date
                  finalDateFormat: option.name === 'Reset' ? '' : joinedFormat,

                  selectedMeasureFormat: formatObject?.selectedMeasure?.name,
                },
              },
              optionKey,
            );
            if (option.name === 'Reset') {
              setSourceMap(new Map());
              setSourcesFormatMapping(new Map());
            }
          }}
          title={option.hintTitle}
        >
          {option.name}
        </div>
      );
    });

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        paddingLeft: '0.5rem',
      }}
    >
      <Typography
        sx={{ fontWeight: '600', marginTop: '0px', paddingTop: '0.5rem', color: '#404040' }}
      >
        Change Date Format
      </Typography>
      <InputLabel
        sx={{
          textAlign: 'left',
          marginTop: '0.25rem',
          fontSize: '0.8rem',
        }}
      >
        Year
      </InputLabel>
      <ButtonGroup size="small" aria-label="year button group">
        {formatMapping.Year.map((item) => (
          <Tooltip
            title={item.description}
            key={item.label}
            slotProps={{
              tooltip: {
                sx: {
                  fontSize: '12px',
                },
              },
            }}
          >
            <Button
              key={item.label}
              sx={{
                textTransform: 'none',
                fontSize: '0.8rem',
                border: '1px solid #999999',
                borderRadius: '0.25rem',
                color: 'black',
                height: '1.3rem',
                backgroundColor: 'inherit',
                '&:hover': {
                  backgroundColor: '#f2f2f2',
                  border: '1px solid #999999',
                },
              }}
              onClick={() => {
                setSourceMap((prev) => {
                  const newMap = new Map(prev);
                  newMap.set('year', item.label);
                  return newMap;
                });
                setSourcesFormatMapping((prev) => {
                  const newMap = new Map(prev);
                  newMap.set('year', item.format);
                  return newMap;
                });
              }}
            >
              {item.label}
            </Button>
          </Tooltip>
        ))}
      </ButtonGroup>
      <InputLabel
        sx={{
          textAlign: 'left',
          marginTop: '0.2rem',
          fontSize: '0.8rem',
        }}
      >
        Month
      </InputLabel>
      <ButtonGroup size="small" aria-label="month button group">
        {formatMapping.Month.map((item) => (
          <Tooltip
            title={item.description}
            slotProps={{
              tooltip: {
                sx: {
                  fontSize: '12px',
                },
              },
            }}
          >
            <Button
              key={item.label}
              sx={{
                textTransform: 'none',
                fontSize: '0.8rem',
                border: '1px solid #999999',
                borderRadius: '0.25rem',
                color: 'black',
                height: '1.3rem',
                backgroundColor: 'inherit',
                '&:hover': {
                  backgroundColor: '#f2f2f2',
                  border: '1px solid #999999',
                },
              }}
              onClick={() => {
                setSourceMap((prev) => {
                  const newMap = new Map(prev);
                  newMap.set('month', item.label);
                  return newMap;
                });
                setSourcesFormatMapping((prev) => {
                  const newMap = new Map(prev);
                  newMap.set('month', item.format);
                  return newMap;
                });
              }}
            >
              {item.label}
            </Button>
          </Tooltip>
        ))}
      </ButtonGroup>
      <InputLabel
        sx={{
          textAlign: 'left',
          marginTop: '0.2rem',
          fontSize: '0.8rem',
        }}
      >
        Day
      </InputLabel>
      <ButtonGroup size="small" aria-label="day button group">
        {formatMapping.Day.map((item) => (
          <Tooltip
            title={item.description}
            slotProps={{
              tooltip: {
                sx: {
                  fontSize: '12px',
                },
              },
            }}
          >
            <Button
              key={item.label}
              sx={{
                textTransform: 'none',
                fontSize: '0.8rem',
                border: '1px solid #999999',
                borderRadius: '0.25rem',
                color: 'black',
                height: '1.3rem',
                backgroundColor: 'inherit',
                '&:hover': {
                  backgroundColor: '#f2f2f2',
                  border: '1px solid #999999',
                },
              }}
              onClick={() => {
                setSourceMap((prev) => {
                  const newMap = new Map(prev);
                  newMap.set('date', item.label);
                  return newMap;
                });
                setSourcesFormatMapping((prev) => {
                  const newMap = new Map(prev);
                  newMap.set('date', item.format);
                  return newMap;
                });
              }}
            >
              {item.label}
            </Button>
          </Tooltip>
        ))}
      </ButtonGroup>
      <InputLabel
        sx={{
          textAlign: 'left',
          marginTop: '0.2rem',
          fontSize: '0.8rem',
        }}
      >
        Separator
      </InputLabel>
      <ButtonGroup size="small" aria-label="separator button group">
        {formatMapping.Separator.map((item) => {
          if (item.label === 'Custom') {
            return editMode ? (
              <input
                style={{
                  width: '3rem',
                  height: '1.3rem',
                  fontSize: '0.8rem',
                  border: '1px solid #2bb9bb',
                  borderRadius: '0.25rem',
                  textAlign: 'center',
                  outline: 'none',
                }}
                autoFocus
                type="text"
                onBlur={(e) => {
                  setEditMode(false);
                  if (sourcesMap.has('separator1')) {
                    setSourceMap((prev) => {
                      const newMap = new Map(prev);
                      newMap.set('separator2', e.target.value);
                      return newMap;
                    });
                    setSourcesFormatMapping((prev) => {
                      const newMap = new Map(prev);
                      newMap.set('separator2', e.target.value);
                      return newMap;
                    });
                  } else {
                    setSourceMap((prev) => {
                      const newMap = new Map(prev);
                      newMap.set('separator1', e.target.value);
                      return newMap;
                    });
                    setSourcesFormatMapping((prev) => {
                      const newMap = new Map(prev);
                      newMap.set('separator1', e.target.value);
                      return newMap;
                    });
                  }
                }}
              />
            ) : (
              <Tooltip
                title={item.description}
                slotProps={{
                  tooltip: {
                    sx: {
                      fontSize: '12px',
                    },
                  },
                }}
              >
                <Button
                  key={item.label}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.8rem',
                    border: '1px solid #999999',
                    borderRadius: '0.25rem',
                    color: 'black',
                    height: '1.3rem',
                    backgroundColor: 'inherit',
                    '&:hover': {
                      backgroundColor: '#f2f2f2',
                      border: '1px solidrgb(2, 2, 2)',
                    },
                  }}
                  onClick={() => {
                    setEditMode(true);
                  }}
                >
                  {item.label}
                </Button>
              </Tooltip>
            );
          }
          return (
            <Tooltip
              title={item.description}
              slotProps={{
                tooltip: {
                  sx: {
                    fontSize: '12px',
                  },
                },
              }}
            >
              <Button
                key={item.label}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.8rem',
                  border: '1px solid #999999',
                  borderRadius: '0.25rem',
                  color: 'black',
                  height: '1.3rem',
                  backgroundColor: 'inherit',
                  '&:hover': {
                    backgroundColor: '#f2f2f2',
                    border: '1px solid #999999',
                  },
                }}
                onClick={() => {
                  if (sourcesMap.has('separator1')) {
                    setSourceMap((prev) => {
                      const newMap = new Map(prev);
                      newMap.set('separator2', item.label);
                      return newMap;
                    });
                    setSourcesFormatMapping((prev) => {
                      const newMap = new Map(prev);
                      newMap.set('separator2', item.label);
                      return newMap;
                    });
                  } else {
                    setSourceMap((prev) => {
                      const newMap = new Map(prev);
                      newMap.set('separator1', item.label);
                      return newMap;
                    });
                    setSourcesFormatMapping((prev) => {
                      const newMap = new Map(prev);
                      newMap.set('separator1', item.label);
                      return newMap;
                    });
                  }
                }}
              >
                {item.label}
              </Button>
            </Tooltip>
          );
        })}
      </ButtonGroup>
      <Box
        sx={{
          border: '1px solid #999999',
          width: '95%',
          marginTop: '0.5rem',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '3rem',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          marginBottom: '1rem',
        }}
      >
        <Typography
          sx={{
            fontSize: '0.8rem',
            color: 'black',
            marginLeft: '0.2rem',
          }}
        >
          Output format
        </Typography>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            width: '100%',
            maxWidth: '14rem',
            flexWrap: 'wrap',
            marginLeft: '0.2rem',
            textAlign: 'center',
          }}
        >
          {Array.from(sourcesMap.values()).length >= 1 ? (
            <>
              {Array.from(sourcesMap.values()).map((source, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '0.5rem',
                    margin: '0.5rem',
                    border: '1px solid #2bb9bb',
                    minWidth: '1.5rem',
                    textAlign: 'center',
                    paddingInline: '0.2rem',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.8rem',
                      color: '#2bb9bb',
                      fontWeight: 'bold',
                    }}
                  >
                    {source}
                  </Typography>
                  <button
                    type="button"
                    className="buttonCommon columnClose"
                    onClick={() => {
                      //key is same in both objects(sourcesMap and sourcesFormatMapping)
                      const key = getMapKeyByValue(sourcesMap, source);
                      setSourceMap((prev) => {
                        const newMap = new Map(prev);
                        newMap.delete(key as string);
                        return newMap;
                      });
                      setSourcesFormatMapping((prev) => {
                        const newMap = new Map(prev);
                        newMap.delete(key as string);
                        return newMap;
                      });
                    }}
                    title="Remove field"
                  >
                    <CloseRoundedIcon style={{ fontSize: '13px' }} />
                  </button>
                </Box>
              ))}
            </>
          ) : (
            <div style={{ fontSize: '11px', color: '#999999', margin: 'auto' }}>
              No format is selected
            </div>
          )}
        </div>
        <div
          style={{
            fontSize: '12px',
            color: '#999999',
            margin: '0.2rem auto',
            display: 'flex',
            flexWrap: 'wrap',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
          }}
        >
          Output format:&nbsp;{Array.from(sourcesMap.values()).join('')}
        </div>
      </Box>
      <div className="radioButtons" style={{ padding: '0', marginLeft: 0 }}>
        {RenderButtons()}
      </div>
    </Box>
  );
};

export default DateFormat;
